import { useCallback, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  ChangeEventInput,
  validateChangeEvent,
} from "@/lib/change-events";
import {
  ChangeReasonPrompt,
  ChangeReasonResult,
} from "@/components/orders/ChangeReasonDialog";

interface PromptResolver {
  resolve: (value: ChangeReasonResult[] | null) => void;
}

/**
 * Imperative, promise-based prompt for one or more `ChangeEventType`s.
 * Consumers render `<ChangeReasonDialog {...dialogProps} />` once and call
 * `promptForReasons([...])` to await user input before persisting a mutation.
 */
export function useChangeEventPrompt() {
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState<ChangeReasonPrompt[]>([]);
  const [orderUniqueId, setOrderUniqueId] = useState<string | null>(null);
  const [title, setTitle] = useState<string | undefined>(undefined);
  const [confirmLabel, setConfirmLabel] = useState<string | undefined>(undefined);
  const resolverRef = useRef<PromptResolver | null>(null);

  const promptForReasons = useCallback(
    (
      prompts: ChangeReasonPrompt[],
      opts?: { orderUniqueId?: string | null; title?: string; confirmLabel?: string },
    ) => {
      // Resolve any in-flight prompt to null before opening a new one.
      resolverRef.current?.resolve(null);
      setEvents(prompts);
      setOrderUniqueId(opts?.orderUniqueId ?? null);
      setTitle(opts?.title);
      setConfirmLabel(opts?.confirmLabel);
      setOpen(true);
      return new Promise<ChangeReasonResult[] | null>((resolve) => {
        resolverRef.current = { resolve };
      });
    },
    [],
  );

  const handleConfirm = useCallback((results: ChangeReasonResult[]) => {
    resolverRef.current?.resolve(results);
    resolverRef.current = null;
    setOpen(false);
  }, []);

  const handleOpenChange = useCallback((value: boolean) => {
    setOpen(value);
    if (!value) {
      resolverRef.current?.resolve(null);
      resolverRef.current = null;
    }
  }, []);

  const dialogProps = {
    open,
    onOpenChange: handleOpenChange,
    events,
    orderUniqueId,
    title,
    confirmLabel,
    onConfirm: handleConfirm,
  };

  return { promptForReasons, dialogProps };
}

/**
 * Persist a batch of `change_events` rows. Each row is validated client-side
 * before insert; the DB trigger validates again as a backstop.
 *
 * Best-effort: callers should `await` but treat failures as non-fatal (the
 * mutation that triggered the prompt has already committed).
 */
export async function recordChangeEvents(
  inputs: ChangeEventInput[],
): Promise<{ inserted: number; error?: string }> {
  if (inputs.length === 0) return { inserted: 0 };
  try {
    inputs.forEach(validateChangeEvent);
  } catch (err: any) {
    return { inserted: 0, error: err?.message ?? String(err) };
  }

  const { data: userData } = await supabase.auth.getUser();
  const userId = userData?.user?.id ?? null;
  const userEmail = userData?.user?.email ?? null;

  const rows = inputs.map((i) => ({
    tenant_id: i.tenantId,
    order_id: i.orderId,
    eapi_trip_id: i.eapiTripId ?? null,
    event_type: i.eventType,
    reason: i.reason,
    other_text: i.otherText ?? null,
    previous_value: i.previousValue ?? null,
    new_value: i.newValue ?? null,
    created_by_user_id: userId,
    created_by_email: userEmail,
  }));

  const { error, count } = await supabase
    .from("change_events" as any)
    .insert(rows as any, { count: "exact" });

  if (error) return { inserted: 0, error: error.message };
  return { inserted: count ?? rows.length };
}